<template>
  <div class="project-home">
    <header class="project-home-header">
      <div class="header-main">
        <router-link to="/projects" class="back-link">← All projects</router-link>
        <h1>{{ project?.name || 'Project' }}</h1>
        <p v-if="project?.description" class="project-desc">{{ project.description }}</p>
        <p class="project-meta">
          <span v-if="project?.default_comprehensive_check_name">
            Check: {{ project.default_comprehensive_check_name }}
          </span>
          <span v-else class="meta-warn">Check: No check assigned</span>
        </p>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="openGeneralUpload">
          Upload
        </button>
        <router-link :to="`/projects/${projectId}/settings`" class="btn btn-ghost">
          Settings
        </router-link>
      </div>
    </header>

    <div v-if="initialLoading" class="state-block">Loading project…</div>
    <div v-else-if="error" class="state-block error">
      <p>{{ error }}</p>
      <button type="button" class="btn btn-primary" @click="loadData">Retry</button>
    </div>

    <template v-else>
      <div class="project-home-bento">
        <div class="bento-left">
          <!-- Metrics: compact pulse — content-sized, not stretched -->
          <section class="bento-metrics">
            <h2 class="bento-label">Compliance pulse</h2>
            <div class="metrics-row">
              <div class="metric">
                <span class="metric-num">{{ pulseTotal }}</span>
                <span class="metric-label">Active {{ entityTerm.plural }}</span>
              </div>
              <div class="metric">
                <span class="metric-num">{{ pulseCompliant }}</span>
                <span class="metric-label">Compliant</span>
                <span v-if="pulseTotal > 0" class="metric-sub">
                  {{ Math.round((pulseCompliant / pulseTotal) * 100) }}%
                </span>
              </div>
              <div class="metric" :class="{ 'metric--alert': pulseNeedAction > 0 }">
                <span class="metric-num">{{ pulseNeedAction }}</span>
                <span class="metric-label">Need action</span>
              </div>
            </div>
          </section>

          <!-- Check Lab under pulse -->
          <section
            class="bento-purpose bento-lab"
            :class="{ 'bento-purpose--alert': !project?.default_comprehensive_check_name }"
          >
            <div class="purpose-icon" aria-hidden="true">
              <i class="fas fa-flask"></i>
            </div>
            <div class="purpose-body">
              <h2>Check Lab</h2>
              <p>
                <template v-if="project?.default_comprehensive_check_name">
                  Inspect and tune
                  <strong>{{ project.default_comprehensive_check_name }}</strong>
                  — the requirements every upload is reviewed against.
                </template>
                <template v-else>
                  No check assigned yet. Assign one in settings before reviews will work.
                </template>
              </p>
              <div class="purpose-actions">
                <router-link
                  v-if="project?.default_comprehensive_check_name"
                  :to="`/projects/${projectId}/lab`"
                  class="btn btn-primary btn-sm"
                >
                  Open Lab
                </router-link>
                <router-link :to="`/projects/${projectId}/settings`" class="btn btn-ghost btn-sm">
                  Configure
                </router-link>
                <router-link
                  v-if="canAccessResearchCampaigns"
                  :to="{ path: '/campaigns', query: { fromProject: projectId } }"
                  class="btn btn-ghost btn-sm"
                >
                  Research campaigns
                </router-link>
              </div>
            </div>
          </section>
        </div>

        <!-- Upload: review against this project's check (matches left stack height) -->
        <section
          class="bento-upload"
          :class="{ 'is-drag-over': uploadDragOver }"
          @dragenter.prevent="uploadDragOver = true"
          @dragover.prevent="uploadDragOver = true"
          @dragleave.prevent="onUploadDragLeave"
          @drop.prevent="onHomeUploadDrop"
          @click="openHomeUploadPicker"
          role="button"
          tabindex="0"
          @keydown.enter.prevent="openHomeUploadPicker"
          @keydown.space.prevent="openHomeUploadPicker"
          :aria-label="uploadAriaLabel"
        >
          <input
            ref="homeUploadInput"
            type="file"
            class="home-upload-input"
            multiple
            :accept="homeUploadAccept"
            @change="onHomeUploadPick"
            @click.stop
          >
          <div class="upload-drop-inner">
            <i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
            <h2>Review a certificate</h2>
            <p class="upload-purpose">
              Drop a COI or supporting document to analyze against
              <strong v-if="project?.default_comprehensive_check_name">
                {{ project.default_comprehensive_check_name }}
              </strong>
              <strong v-else>this project’s comprehensive check</strong>
              for this project.
            </p>
            <p class="upload-hint">or click to browse · PDF, DOC, DOCX</p>
          </div>
        </section>

        <!-- Batch intake: project-scoped; visual weight matches capability -->
        <section class="bento-purpose bento-batch">
          <div class="purpose-icon" aria-hidden="true">
            <i class="fas fa-inbox"></i>
          </div>
          <div class="purpose-body">
            <h2>Batch intake</h2>
            <p>
              Collect many certificates for <strong>this project</strong> via a secure
              upload link, review the inbox, then process into compliance.
            </p>
            <ul class="purpose-keypoints">
              <li>Project-scoped secure links (not org-wide)</li>
              <li>Review uploads before processing</li>
              <li>Bulk process against this project’s check</li>
              <li>AI analysis cost applies when you process</li>
            </ul>
            <div class="purpose-actions">
              <router-link
                :to="`/projects/${projectId}/batch-tools`"
                class="btn btn-primary btn-sm"
              >
                Open batch intake
              </router-link>
              <router-link
                :to="`/projects/${projectId}/batch-tools#reprocess`"
                class="btn btn-ghost btn-sm"
              >
                Batch reprocess
              </router-link>
            </div>
          </div>
        </section>

        <!-- Match hygiene: project-scoped assignment DQ -->
        <section
          class="bento-purpose bento-hygiene"
          :class="{ 'bento-purpose--alert': matchHygieneOpenCount > 0 }"
        >
          <div class="purpose-icon" aria-hidden="true">
            <i class="fas fa-link"></i>
          </div>
          <div class="purpose-body">
            <h2>Match hygiene</h2>
            <p>
              Fix who certificates are assigned to in <strong>this project</strong> —
              wrong location or entity, party vs session mismatch, duplicates.
              Rematch without re-running comprehensive checks.
            </p>
            <ul v-if="matchHygieneOpenCount > 0" class="purpose-keypoints">
              <li>{{ matchHygieneOpenCount }} open assignment issue{{ matchHygieneOpenCount === 1 ? '' : 's' }}</li>
            </ul>
            <div class="purpose-actions">
              <router-link
                :to="`/projects/${projectId}/match-hygiene`"
                class="btn btn-primary btn-sm"
              >
                Open match hygiene
              </router-link>
            </div>
          </div>
        </section>

        <section class="bento-purpose bento-mail">
          <div class="purpose-icon" aria-hidden="true">
            <i class="fas fa-envelope-open-text"></i>
          </div>
          <div class="purpose-body">
            <h2>Email delivery</h2>
            <p>
              See whether gap emails for this project reached people, and how
              many stores sent a certificate back. Bounce and spam rates are
              scored against the limits mailbox providers enforce.
            </p>
            <div class="purpose-actions">
              <router-link
                :to="`/projects/${projectId}/compliance-health/email-delivery`"
                class="btn btn-primary btn-sm"
              >
                Open email delivery
              </router-link>
            </div>
          </div>
        </section>

        <!-- More tools (secondary) -->
        <section class="bento-purpose bento-purpose--tools bento-tools">
          <div class="purpose-icon" aria-hidden="true">
            <i class="fas fa-th-large"></i>
          </div>
          <div class="purpose-body">
            <h2>More tools</h2>
            <ul class="tool-links">
              <li>
                <router-link :to="`/projects/${projectId}/documents`">Documents</router-link>
              </li>
              <li>
                <router-link :to="`/projects/${projectId}/compliance`">Compliance intelligence</router-link>
              </li>
              <li>
                <router-link :to="`/projects/${projectId}/rag-analysis`">RAG analysis</router-link>
              </li>
              <li>
                <router-link :to="`/projects/${projectId}/franchise-data`">Franchise data</router-link>
              </li>
              <li v-if="canAccessAbTesting">
                <router-link to="/ab-testing">A/B testing</router-link>
              </li>
            </ul>
          </div>
        </section>

        <!-- Agent -->
        <section class="bento-purpose bento-agent">
          <div class="purpose-icon" aria-hidden="true">
            <i class="fas fa-comments"></i>
          </div>
          <div class="purpose-body">
            <h2>Agent</h2>
            <p>Ask about portfolio issues, expired policies, and next actions for this project.</p>
            <button type="button" class="btn btn-primary btn-sm" @click.stop="openAgentChat">
              Open agent
            </button>
          </div>
        </section>
      </div>

      <!-- Companies list -->
      <section id="companies-section" class="companies-section">
        <header class="companies-header">
          <h2>{{ entityTerm.plural }}</h2>
          <p>Track and manage compliance for this project</p>
        </header>

        <EntityList
          ref="entityListRef"
          :entities="entities"
          :loading="loadingEntities"
          :total-count="totalCount"
          :current-page="currentPage"
          :page-size="pageSize"
          :entity-term="entityTerm"
          :location-term="locationTerm"
          :highlighted-subject-id="highlightedSubjectId"
          @add-entity="showAddEntityModal = true"
          @search="handleSearch"
          @filter="handleFilter"
          @page-change="handlePageChange"
          @page-size-change="handlePageSizeChange"
          @view-requirements="handleViewRequirements"
          @add-store="handleAddStore"
          @upload-document="handleUploadForEntity"
          @request-documents="handleRequestDocuments"
          @request-from-agent="handleRequestFromAgent"
          @view-analysis="handleViewAnalysis"
          @archive-entity="handleArchiveEntity"
          @entity-updated="handleEntityUpdated"
        />
      </section>
    </template>

    <!-- Smart Upload Modal -->
    <SmartUploadModal
      :show="showUploadModal"
      :project-id="projectId"
      :entities="entities"
      :entity-term="entityTerm"
      :initial-subject-id="uploadInitialSubjectId"
      :initial-files="uploadInitialFiles"
      @show-entity-extraction="handleShowEntityExtraction"
      @upload-complete="handleUploadComplete"
      @close="closeUploadModal"
    />

    <!-- Entity Extraction Modal -->
    <EntityExtractionModal
      v-if="showEntityExtraction && extractionData"
      :show="showEntityExtraction"
      :project-id="projectId"
      :document-id="extractionData.documentId"
      :document-name="extractionData.documentName"
      :document-url="extractionData.documentUrl"
      :extracted-entities="extractionData.extractedEntities"
      :subject-id="extractionData.subjectId || null"
      :entity-term="entityTerm"
      :location-term="locationTerm"
      :match-strategy="extractionData.matchStrategy"
      @entities-confirmed="handleEntitiesConfirmed"
      @close="showEntityExtraction = false"
    />

    <!-- Analysis Results Modal -->
    <AnalysisResultsModal
      v-if="showAnalysisResults && analysisData"
      :show="showAnalysisResults"
      :check-run-id="analysisData.checkRunId"
      :document-id="analysisData.documentId"
      @close="showAnalysisResults = false"
    />

    <!-- Add Entity Modal -->
    <AddComplianceSubjectModal
      :show="showAddEntityModal"
      :project-id="projectId"
      :entity-term-singular="entityTerm.singular"
      :entity-term-plural="entityTerm.plural"
      @close="showAddEntityModal = false"
      @subject-added="handleSubjectAdded"
    />

    <!-- Request Documents Modal -->
    <div v-if="showRequestDocsModal" class="modal-overlay" @click.self="closeRequestDocsModal">
      <div class="modal-container">
        <UploadRequestForm
          :project-id="projectId"
          :document-types="[]"
          :workflows="[]"
          :subject-context="subjectForRequest"
          @request-sent="handleRequestSent"
          @close="closeRequestDocsModal"
        />
        <button @click="closeRequestDocsModal" class="modal-close-btn">&times;</button>
      </div>
    </div>

    <!-- Compliance Agent Chat -->
    <ComplianceAgentChat
      ref="agentChatRef"
      :project-id="projectId"
      :summary="complianceSummary"
      @filter-entities="handleAgentFilter"
      @select-entities="handleAgentSelectEntities"
      @refresh-data="loadEntities"
    />
    
    <!-- Add Store Modal -->
    <div v-if="showAddStoreModal" class="modal-overlay" @click.self="cancelAddStore">
      <div class="modal-dialog add-store-modal">
        <div class="modal-header">
          <h3><i class="fas fa-store-alt"></i> Add Store to {{ addStoreEntityName }}</h3>
          <button @click="cancelAddStore" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="form-section">
            <h4><i class="fas fa-map-marker-alt"></i> Store Information</h4>

            <div class="form-group">
              <label>Store / Location Number <span class="required">*</span></label>
              <input
                v-model="addStoreForm.store_number"
                type="text"
                placeholder="e.g., 1001A"
                class="form-control"
              />
              <small class="form-hint">The unique store number or ID for this location</small>
            </div>

            <div class="form-group">
              <label>Store Name <span class="form-optional">(Optional)</span></label>
              <input
                v-model="addStoreForm.location_name"
                type="text"
                placeholder="e.g., Downtown Toronto"
                class="form-control"
              />
            </div>

            <AddressFields v-model="addStoreForm.address" prefix="add-store" />
          </div>
          
          <div class="form-section">
            <h4><i class="fas fa-user-tie"></i> Insurance Agent (Optional)</h4>
            
            <div class="form-group">
              <label>Select Agent</label>
              <select v-model="addStoreForm.agent_id" class="form-control">
                <option value="">-- No Agent --</option>
                <option v-for="agent in availableAgents" :key="agent.id" :value="agent.id">
                  {{ agent.name }} ({{ agent.primary_email }})
                </option>
              </select>
            </div>
          </div>
          
          <div class="info-note">
            <i class="fas fa-info-circle"></i>
            <span>This will create a new location and compliance subject for this entity in the current project.</span>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="cancelAddStore" class="btn btn-secondary">
            Cancel
          </button>
          <button 
            @click="submitAddStore" 
            class="btn btn-primary"
            :disabled="addingStore || !addStoreForm.store_number.trim()"
          >
            <i v-if="addingStore" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-plus"></i>
            {{ addingStore ? 'Adding...' : 'Add Store' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import api from '@/services/api';
import auth from '@/services/auth';
import { UI_CONSTANTS } from '@/constants';
import { useProjectTerminology } from '@/composables/useProjectTerminology';
import EntityList from '@/components/compliance/EntityList.vue';
import SmartUploadModal from '@/components/compliance/SmartUploadModal.vue';
import EntityExtractionModal from '@/components/compliance/EntityExtractionModal.vue';
import AnalysisResultsModal from '@/components/compliance/AnalysisResultsModal.vue';
import AddComplianceSubjectModal from '@/components/comprehensive/AddComplianceSubjectModal.vue';
import AddressFields from '@/components/shared/AddressFields.vue';
import UploadRequestForm from '@/components/UploadRequestForm.vue';
import ComplianceAgentChat from '@/components/compliance/ComplianceAgentChat.vue';

export default {
  name: 'ComplianceDashboard',
  components: {
    EntityList,
    AnalysisResultsModal,
    SmartUploadModal,
    EntityExtractionModal,
    AddComplianceSubjectModal,
    UploadRequestForm,
    ComplianceAgentChat,
    AddressFields,
  },
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    
    // State
    const project = ref(null);
    const entities = ref([]);
    const initialLoading = ref(true);
    const loadingEntities = ref(false);
    const error = ref(null);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const totalCount = ref(0);
    const searchQuery = ref('');
    const statusFilter = ref('');
    const showUploadModal = ref(false);
    const showAddEntityModal = ref(false);
    const showEntityExtraction = ref(false);
    const extractionData = ref(null);
    const showAnalysisResults = ref(false);
    const analysisData = ref(null);
    const highlightedSubjectId = ref(null);
    const showRequestDocsModal = ref(false);
    const subjectForRequest = ref(null);
    const uploadInitialSubjectId = ref(null);
    const uploadInitialFiles = ref([]);
    const uploadDragOver = ref(false);
    const homeUploadInput = ref(null);
    const homeUploadAccept = UI_CONSTANTS.COI_UPLOAD_ACCEPT || '.pdf,.doc,.docx';
    
    // Add Store modal state
    const showAddStoreModal = ref(false);
    const addingStore = ref(false);
    const addStoreEntityId = ref(null);
    const addStoreEntityName = ref('');
    const addStoreForm = ref({
      store_number: '',
      location_name: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        province: '',
        postal_code: '',
        country: 'US',
      },
      agent_id: ''
    });
    const availableAgents = ref([]);
    
    // Terminology
    const { entityTerm, locationTerm } = useProjectTerminology(project);

    const canAccessAbTesting = computed(() => (
      auth.hasAnyRole(['PLATFORM_ADMIN', 'PLATFORM_ENGINEER'])
    ));

    const canAccessResearchCampaigns = computed(() => (
      auth.hasAnyRole([
        'PLATFORM_ADMIN',
        'PLATFORM_ENGINEER',
        'ORG_ADMIN',
        'HEALTHCARE_ADMIN',
        'COMPLIANCE_ADMIN',
        'ADMIN',
        'COMPLIANCE_OPS',
      ])
    ));
    
    // Pulse = active universe (excludes ARCHIVED + INACTIVE)
    const pulseTotal = ref(0);
    const pulseCompliant = ref(0);
    const pulseNeedAction = ref(0);
    const matchHygieneOpenCount = ref(0);

    // Page-local tallies (current EntityList page only — not for pulse)
    const pageCompliantCount = computed(() => {
      return entities.value.filter(e => e.current_status === 'COMPLIANT').length;
    });

    const pageNeedActionCount = computed(() => {
      return entities.value.filter(e =>
        ['NON_COMPLIANT', 'ACTION_NEEDED', 'PARTIALLY_COMPLIANT'].includes(e.current_status)
      ).length;
    });

    // Summary object for the agent chat context — prefer active pulse
    const complianceSummary = computed(() => ({
      total_entities: pulseTotal.value || totalCount.value,
      compliant_count: pulseCompliant.value || pageCompliantCount.value,
      non_compliant_count: pulseNeedAction.value || pageNeedActionCount.value,
      compliance_rate: pulseTotal.value > 0
        ? Math.round((pulseCompliant.value / pulseTotal.value) * 100)
        : 0,
      project_name: project.value?.name || 'Unknown Project'
    }));

    const uploadAriaLabel = computed(() => {
      const check = project.value?.default_comprehensive_check_name;
      const against = check
        ? `against ${check}`
        : 'against this project’s comprehensive check';
      return `Review a certificate ${against} — drag and drop or click to browse`;
    });
    
    // Methods
    const loadProject = async () => {
      try {
        const response = await api.get(`/projects/${props.projectId}/`);
        project.value = response.data.project || response.data;
      } catch (err) {
        console.error('Error loading project:', err);
        error.value = 'Failed to load project information';
      }
    };

    const loadPulse = async () => {
      try {
        const response = await api.get('/compliance-subjects/pulse/', {
          params: { project: props.projectId },
        });
        const data = response.data || {};
        pulseTotal.value = data.total ?? 0;
        pulseCompliant.value = data.compliant ?? 0;
        pulseNeedAction.value = data.need_action ?? 0;
      } catch (err) {
        console.error('Error loading compliance pulse:', err);
        // Leave previous pulse values; don't block the page
      }
    };
    
    const loadEntities = async () => {
      loadingEntities.value = true;
      try {
        let response;
        
        // Use dedicated search endpoint if searching
        if (searchQuery.value && searchQuery.value.trim().length >= 2) {
          response = await api.get(`/projects/${props.projectId}/entities/search/`, {
            params: {
              q: searchQuery.value,
              page: currentPage.value,
              page_size: pageSize.value,
              ...(statusFilter.value && { status: statusFilter.value }),
              ...(statusFilter.value === 'ARCHIVED' && { include_archived: 'true' })
            }
          });
          
          // Handle search response format (uses 'subjects' key)
          if (response.data.subjects && Array.isArray(response.data.subjects)) {
            entities.value = response.data.subjects;
            totalCount.value = response.data.total || response.data.subjects.length;
          } else if (response.data.results && Array.isArray(response.data.results)) {
            entities.value = response.data.results;
            totalCount.value = response.data.count || entities.value.length;
          } else if (Array.isArray(response.data)) {
            entities.value = response.data;
            totalCount.value = entities.value.length;
          } else {
            console.warn('Unexpected search response format:', response.data);
            entities.value = [];
            totalCount.value = 0;
          }
        } else {
          // Regular list endpoint when not searching
          response = await api.get(`/compliance-subjects/`, {
            params: {
              project: props.projectId,
              page: currentPage.value,
              page_size: pageSize.value,
              ordering: '-updated_at',
              ...(statusFilter.value && { current_status: statusFilter.value }),
              ...(statusFilter.value === 'ARCHIVED' && { include_archived: 'true' })
            }
          });
          entities.value = response.data.results || response.data;
          totalCount.value = response.data.count || entities.value.length;
        }
      } catch (err) {
        console.error('Error loading entities:', err);
        error.value = 'Failed to load entities';
      } finally {
        loadingEntities.value = false;
      }
    };
    
    const loadMatchHygieneOpenCount = async () => {
      const pid = parseInt(props.projectId, 10);
      if (!Number.isFinite(pid)) {
        matchHygieneOpenCount.value = 0;
        return;
      }
      try {
        const response = await api.get('/match-hygiene/issues/', {
          params: {
            project_id: pid,
            status: 'open',
            limit: 1,
            offset: 0,
          },
        });
        matchHygieneOpenCount.value = response.data?.data?.open_count || 0;
      } catch {
        matchHygieneOpenCount.value = 0;
      }
    };

    const loadData = async () => {
      initialLoading.value = true;
      error.value = null;
      
      try {
        await Promise.all([
          loadProject(),
          loadEntities(),
          loadPulse(),
          loadMatchHygieneOpenCount(),
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        initialLoading.value = false;
      }
    };
    
    const handleSearch = (query) => {
      searchQuery.value = query;
      currentPage.value = 1;
      loadEntities();
    };
    
    const handleFilter = (filter) => {
      statusFilter.value = filter;
      currentPage.value = 1;
      loadEntities();
    };
    
    const handlePageChange = (page) => {
      currentPage.value = page;
      loadEntities();
    };
    
    const handlePageSizeChange = (size) => {
      pageSize.value = size;
      currentPage.value = 1;
      loadEntities();
    };
    
    const handleViewRequirements = (entity) => {
      console.log('View requirements for:', entity);
      // Navigate to comprehensive analysis dashboard for this subject
      router.push({
        path: `/projects/${props.projectId}/compliance`,
        query: {
          subject: entity.id,
          autoExpand: 'true'
        }
      });
    };
    
    const openGeneralUpload = () => {
      uploadInitialSubjectId.value = null;
      uploadInitialFiles.value = [];
      showUploadModal.value = true;
    };

    const openHomeUploadPicker = () => {
      homeUploadInput.value?.click();
    };

    const onUploadDragLeave = (event) => {
      // Only clear when leaving the drop zone itself
      if (event.currentTarget.contains(event.relatedTarget)) return;
      uploadDragOver.value = false;
    };

    const openUploadWithFiles = (files) => {
      const list = Array.from(files || []).filter(Boolean);
      uploadInitialSubjectId.value = null;
      uploadInitialFiles.value = list;
      uploadDragOver.value = false;
      showUploadModal.value = true;
    };

    const onHomeUploadDrop = (event) => {
      openUploadWithFiles(event.dataTransfer?.files);
    };

    const onHomeUploadPick = (event) => {
      openUploadWithFiles(event.target?.files);
      if (event.target) event.target.value = '';
    };

    const handleUploadForEntity = (entity) => {
      uploadInitialSubjectId.value = entity?.id ?? null;
      uploadInitialFiles.value = [];
      showUploadModal.value = true;
    };

    const closeUploadModal = () => {
      showUploadModal.value = false;
      uploadInitialSubjectId.value = null;
      uploadInitialFiles.value = [];
    };
    
    const handleRequestDocuments = (entity) => {
      console.log('Request documents from:', entity);
      if (!entity || !entity.subject_entity) {
        console.error('Cannot open request modal: Invalid entity data');
        alert('Cannot request documents: missing entity information');
        return;
      }
      subjectForRequest.value = entity;
      showRequestDocsModal.value = true;
    };
    
    const closeRequestDocsModal = () => {
      showRequestDocsModal.value = false;
      subjectForRequest.value = null;
    };
    
    const handleRequestSent = (result) => {
      console.log('Request sent:', result);
      if (result.success) {
        alert(result.message || 'Document request sent successfully!');
        closeRequestDocsModal();
      } else {
        alert(result.message || 'Failed to send document request');
      }
    };
    
    const handleRequestFromAgent = ({ entity, agent }) => {
      console.log('Request from agent:', agent, 'for entity:', entity);
      // Open request modal with agent context
      if (entity && entity.subject_entity) {
        subjectForRequest.value = entity;
        showRequestDocsModal.value = true;
      }
    };
    
    const handleViewAnalysis = async (data) => {
      if (!data?.run?.id) return;

      let documentId = data.run.document_id || data.run.document?.id;

      if (!documentId) {
        try {
          const runResponse = await api.get(`/comprehensive-check-runs/${data.run.id}/`);
          documentId = runResponse.data.document?.id;
        } catch (error) {
          console.error('Could not resolve document for check run:', error);
          alert('Error loading analysis results. Please try again.');
          return;
        }
      }

      if (!documentId) {
        alert('No analysis results found for this session.');
        return;
      }

      analysisData.value = {
        checkRunId: String(data.run.id),
        documentId,
      };
      showAnalysisResults.value = true;
    };
    
    const handleAddStore = async (entity) => {
      console.log('Add store to entity:', entity);
      addStoreEntityId.value = entity.subject_entity_id || entity.subject_entity?.id;
      addStoreEntityName.value = entity.subject_entity_name || entity.subject_entity?.name || 'Entity';
      
      // Load agents if not already loaded
      if (availableAgents.value.length === 0) {
        await loadAvailableAgents();
      }
      
      showAddStoreModal.value = true;
    };
    
    const loadAvailableAgents = async () => {
      try {
        const response = await api.get('/entities/', {
          params: {
            entity_type: 'AGENCY',
            limit: 100
          }
        });
        availableAgents.value = (response.data.results || response.data || []).filter(Boolean);
      } catch (error) {
        console.error('Error loading agents:', error);
      }
    };
    
    const cancelAddStore = () => {
      showAddStoreModal.value = false;
      addStoreForm.value = {
        store_number: '',
        location_name: '',
        address: {
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          province: '',
          postal_code: '',
          country: 'US',
        },
        agent_id: ''
      };
    };
    
    const submitAddStore = async () => {
      if (!addStoreForm.value.store_number.trim()) {
        alert('Store number is required');
        return;
      }
      
      addingStore.value = true;
      
      try {
        const addr = addStoreForm.value.address;
        const response = await api.post('/compliance-health/entities/add-store/', {
          entity_id: addStoreEntityId.value,
          project_id: route.params.id,
          store_number: addStoreForm.value.store_number.trim(),
          location_name: addStoreForm.value.location_name.trim() || null,
          address_line1: addr.address_line1?.trim() || null,
          address_line2: addr.address_line2?.trim() || null,
          city: addr.city?.trim() || null,
          state: addr.state?.trim() || null,
          province: addr.province?.trim() || null,
          postal_code: addr.postal_code?.trim() || null,
          country: addr.country || null,
          agent_id: addStoreForm.value.agent_id || null
        });
        
        if (response.data.success) {
          alert(response.data.message || 'Store added successfully');
          cancelAddStore();
          // Refresh the entities list
          await loadEntities();
        }
      } catch (error) {
        console.error('Error adding store:', error);
        alert('Failed to add store: ' + (error.response?.data?.error || error.message));
      } finally {
        addingStore.value = false;
      }
    };
    
    const handleArchiveEntity = async (entity) => {
      if (!entity || !entity.id) return;
      
      try {
        // Archive the compliance subject by setting its status to ARCHIVED
        await api.patch(`/compliance-subjects/${entity.id}/`, {
          current_status: 'ARCHIVED'
        });
        
        // Refresh the entity list
        await loadEntities();
        
        // Show success notification
        const entityName = entity.subject_entity_name || 'Entity';
        alert(`${entityName} has been archived successfully.`);
      } catch (error) {
        console.error('Error archiving entity:', error);
        alert('Failed to archive entity. Please try again.');
      }
    };
    
    const handleShowEntityExtraction = (data) => {
      extractionData.value = data;
      showEntityExtraction.value = true;
      showUploadModal.value = false;
    };
    
    const handleUploadComplete = (data) => {
      console.log('Upload complete (skip processing):', data);
      showUploadModal.value = false;
      
      // Show success notification
      if (data.message) {
        alert(data.message);
      }
      
      // Refresh documents list (entities may have new documents)
      loadEntities();
    };
    
    const handleEntitiesConfirmed = async (result) => {
      showEntityExtraction.value = false;

      await loadEntities();

      const analysisRuns = result?.analysis_runs || [];
      if (analysisRuns.length > 0) {
        const firstRun = analysisRuns[0];
        analysisData.value = {
          checkRunId: String(firstRun.check_run_id),
          documentId: firstRun.document_id || result.document_id,
        };
        showAnalysisResults.value = true;
        return;
      }

      if (result?.subjects_created > 0) {
        alert(
          `Successfully created ${result.subjects_created} ${
            result.subjects_created === 1 ? entityTerm.value.singular : entityTerm.value.plural
          }`
        );
      }
    };
    
    const handleSubjectAdded = async (newSubject) => {
      console.log('Subject added:', newSubject);
      showAddEntityModal.value = false;
      
      // Refresh entity list
      await loadEntities();
      
      // Show success notification
      const entityName = newSubject?.entity?.name || newSubject?.name || 'New entity';
      alert(`Successfully added ${entityTerm.value.singular}: ${entityName}`);
    };
    
    const handleEntityUpdated = async () => {
      await loadEntities();
    };

    const handleAgentFilter = (filter) => {
      console.log('Agent filter:', filter);
      // Apply filter from agent
      if (filter.status) {
        statusFilter.value = filter.status;
        currentPage.value = 1;
        loadEntities();
      }
    };
    
    const handleAgentSelectEntities = (entityIds) => {
      console.log('Agent selected entities:', entityIds);
      // Highlight first entity if available
      if (entityIds && entityIds.length > 0) {
        highlightedSubjectId.value = entityIds[0];
      }
    };
    
    // Handle subject query parameter - fetch specific subject and highlight it
    const handleSubjectQueryParam = async () => {
      const subjectId = route.query.subject;
      if (!subjectId) return;
      
      console.log('Subject query param detected:', subjectId);
      highlightedSubjectId.value = parseInt(subjectId);
      
      try {
        // Fetch the specific subject directly
        const response = await api.get(`/compliance-subjects/${subjectId}/`);
        const subject = response.data;
        
        if (subject) {
          console.log('Found subject:', subject);
          
          // Check if subject is already in the entities list
          const existingIndex = entities.value.findIndex(e => e.id === subject.id);
          if (existingIndex === -1) {
            // Add the subject to the beginning of the list so it's visible
            entities.value = [subject, ...entities.value];
          }
          
          // The EntityList component will handle scrolling to the highlighted entity
        }
      } catch (err) {
        console.error('Error fetching subject:', err);
      }
    };
    
    const entityListRef = ref(null);
    const agentChatRef = ref(null);

    const openAgentChat = () => {
      if (agentChatRef.value?.openChat) {
        agentChatRef.value.openChat();
      } else if (agentChatRef.value?.toggleChat && !agentChatRef.value.isExpanded) {
        agentChatRef.value.toggleChat();
      }
    };

    // Lifecycle
    onMounted(async () => {
      await loadData();
      // After data is loaded, check for subject query param
      await handleSubjectQueryParam();
    });
    
    // Watch for route query changes (in case user navigates while on the page)
    watch(() => route.query.subject, async (newSubjectId) => {
      if (newSubjectId) {
        await handleSubjectQueryParam();
      }
    });
    
    return {
      project,
      entities,
      initialLoading,
      loadingEntities,
      error,
      currentPage,
      pageSize,
      totalCount,
      searchQuery,
      statusFilter,
      showUploadModal,
      uploadInitialSubjectId,
      uploadInitialFiles,
      uploadDragOver,
      homeUploadInput,
      homeUploadAccept,
      openGeneralUpload,
      openHomeUploadPicker,
      onUploadDragLeave,
      onHomeUploadDrop,
      onHomeUploadPick,
      closeUploadModal,
      showAddEntityModal,
      showEntityExtraction,
      extractionData,
      showAnalysisResults,
      analysisData,
      entityTerm,
      locationTerm,
      canAccessAbTesting,
      canAccessResearchCampaigns,
      pulseTotal,
      pulseCompliant,
      pulseNeedAction,
      matchHygieneOpenCount,
      uploadAriaLabel,
      entityListRef,
      agentChatRef,
      openAgentChat,
      highlightedSubjectId,
      showRequestDocsModal,
      subjectForRequest,
      loadData,
      loadEntities,
      handleSearch,
      handleFilter,
      handlePageChange,
      handlePageSizeChange,
      handleViewRequirements,
      handleAddStore,
      handleUploadForEntity,
      handleRequestDocuments,
      handleRequestFromAgent,
      handleViewAnalysis,
      handleArchiveEntity,
      handleShowEntityExtraction,
      handleUploadComplete,
      handleEntitiesConfirmed,
      handleSubjectAdded,
      closeRequestDocsModal,
      handleRequestSent,
      handleAgentFilter,
      handleAgentSelectEntities,
      handleEntityUpdated,
      complianceSummary,
      // Add Store modal
      showAddStoreModal,
      addingStore,
      addStoreEntityName,
      addStoreForm,
      availableAgents,
      cancelAddStore,
      submitAddStore
    };
  }
};
</script>

<style scoped>
.project-home {
  max-width: 1200px;
  margin: 0;
  padding: 0 0 2rem;
}

.project-home-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.back-link {
  display: inline-block;
  font-size: 0.875rem;
  color: #475569;
  text-decoration: none;
}

.back-link:hover {
  color: var(--p-accent, #1e3a5f);
}

.project-home-header h1 {
  margin: 0.25rem 0 0.35rem;
  font-size: 1.85rem;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
}

.project-desc {
  margin: 0 0 0.35rem;
  font-size: 0.95rem;
  color: #334155;
  max-width: 42rem;
}

.project-meta {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
}

.meta-warn {
  color: #92400e;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
  padding-top: 1.5rem;
}

.state-block {
  padding: 2rem 0;
  color: #475569;
}

.state-block.error {
  color: #b91c1c;
}

.project-home-bento {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-areas:
    "left  left  left  upload upload upload"
    "batch batch hygiene hygiene tools agent"
    "mail  mail  mail  mail   mail  mail";
  gap: 0.85rem;
  margin-bottom: 1.75rem;
  align-items: stretch;
}

.bento-left {
  grid-area: left;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
}

/* Quiet panel shell — not entity-card left-edge chrome */
.bento-metrics,
.bento-upload,
.bento-purpose {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
}

.bento-metrics {
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Compact — height follows content */
  flex: 0 0 auto;
}

.bento-upload {
  grid-area: upload;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-style: dashed;
  border-width: 2px;
  border-color: #cbd5e1;
  background: #fff;
  min-height: 100%;
  transition: border-color 0.15s ease, background 0.15s ease;
  user-select: none;
}

.bento-upload:hover,
.bento-upload.is-drag-over {
  border-color: var(--p-accent, #1e3a5f);
  background: #f8fafc;
}

.bento-upload:focus-visible {
  outline: 2px solid var(--p-accent, #1e3a5f);
  outline-offset: 2px;
}

.home-upload-input {
  display: none;
}

.upload-drop-inner {
  text-align: center;
  padding: 0.75rem 1rem;
  max-width: 22rem;
}

.upload-drop-inner i {
  font-size: 1.75rem;
  color: var(--p-accent, #1e3a5f);
  margin-bottom: 0.55rem;
  display: block;
}

.upload-drop-inner h2 {
  margin: 0 0 0.4rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
}

.upload-purpose {
  margin: 0 0 0.55rem;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.4;
}

.upload-purpose strong {
  color: #0f172a;
  font-weight: 600;
}

.upload-hint {
  margin: 0;
  font-size: 0.82rem;
  color: #64748b;
}

.bento-batch {
  grid-area: batch;
}

.bento-hygiene {
  grid-area: hygiene;
}

.bento-tools {
  grid-area: tools;
}

.bento-agent {
  grid-area: agent;
}

.bento-mail {
  grid-area: mail;
}

.bento-label {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.metrics-row {
  display: flex;
  gap: 0;
  flex-wrap: wrap;
}

.metric {
  flex: 1;
  min-width: 5.5rem;
  padding: 0 1.1rem;
  border-right: 1px solid #e2e8f0;
}

.metric:first-child {
  padding-left: 0;
}

.metric:last-child {
  border-right: none;
  padding-right: 0;
}

.metric-num {
  display: block;
  font-size: 1.85rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
}

.metric-label {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.85rem;
  color: #475569;
}

.metric-sub {
  display: block;
  font-size: 0.8rem;
  color: #64748b;
}

.metric--alert .metric-num,
.metric--alert .metric-label {
  color: #b45309;
}

/* Purpose tiles — icon makes job scannable */
.bento-purpose {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  align-items: start;
  min-height: 8.25rem;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.bento-purpose--alert .purpose-icon {
  background: #fff7ed;
  color: #c2410c;
}

.purpose-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  height: 100%;
}

.purpose-body h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
}

.purpose-body > p {
  margin: 0;
  font-size: 0.88rem;
  color: #475569;
  line-height: 1.35;
}

.purpose-keypoints {
  list-style: none;
  margin: 0.15rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.purpose-keypoints li {
  position: relative;
  padding-left: 0.85rem;
  font-size: 0.82rem;
  color: #475569;
  line-height: 1.3;
}

.purpose-keypoints li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.45em;
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 50%;
  background: var(--p-accent, #1e3a5f);
}

.purpose-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: auto;
  padding-top: 0.35rem;
}

.purpose-body > .btn {
  margin-top: auto;
  align-self: flex-start;
}

.bento-purpose--tools .tool-links {
  list-style: none;
  margin: 0.15rem 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.35rem;
}

.tool-links a {
  color: var(--p-accent, #1e3a5f);
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
}

.tool-links a:hover {
  text-decoration: underline;
}

.companies-section {
  margin-top: 0.5rem;
}

.companies-header {
  margin-bottom: 0.75rem;
}

.companies-header h2 {
  margin: 0 0 0.2rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
}

.companies-header p {
  margin: 0;
  color: #475569;
  font-size: 0.9rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  padding: 0.5rem 0.85rem;
  line-height: 1.2;
  background: transparent;
}

.btn-sm {
  padding: 0.4rem 0.7rem;
  font-size: 0.85rem;
}

.btn-primary {
  background: var(--p-accent, #1e3a5f);
  color: #fff;
  border-color: var(--p-accent, #1e3a5f);
}

.btn-primary:hover {
  filter: brightness(1.05);
  color: #fff;
}

.btn-ghost {
  background: transparent;
  color: #334155;
  border-color: #cbd5e1;
}

.btn-ghost:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
  border-color: #cbd5e1;
}

/* Modals (preserved) */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 12px;
  max-width: 780px;
  width: 92%;
  max-height: 92vh;
  overflow-y: auto;
  position: relative;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
}

.modal-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  line-height: 1;
  border-radius: 4px;
}

.modal-close-btn:hover {
  color: #1a202c;
  background: #f1f5f9;
}

.add-store-modal {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
}

.modal-dialog .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-dialog .modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-dialog .btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.modal-dialog .btn-close:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.modal-dialog .modal-body {
  padding: 1.5rem;
}

.modal-dialog .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.form-section {
  margin-bottom: 1.5rem;
}

.form-section h4 {
  margin: 0 0 1rem;
  font-size: 1rem;
  color: #374151;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.35rem;
  color: #374151;
}

.form-control {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font: inherit;
}

.form-hint {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.form-optional {
  font-weight: 400;
  color: #9ca3af;
}

.required {
  color: #dc2626;
}

.info-note {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #475569;
}

@media (max-width: 960px) {
  .project-home-bento {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "left left"
      "upload upload"
      "batch batch"
      "hygiene hygiene"
      "tools agent"
      "mail mail";
  }

  .bento-upload {
    min-height: 11rem;
  }

  .bento-purpose--tools .tool-links {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .project-home-header {
    flex-direction: column;
  }

  .header-actions {
    padding-top: 0;
  }

  .project-home-bento {
    grid-template-columns: 1fr;
    grid-template-areas:
      "left"
      "upload"
      "batch"
      "hygiene"
      "tools"
      "agent"
      "mail";
  }

  .metric {
    border-right: none;
    padding: 0.35rem 0;
    min-width: 30%;
  }

  .bento-purpose--tools .tool-links {
    grid-template-columns: 1fr;
  }
}
</style>
